import React, { useState, useEffect, useRef } from 'react';
import './AudioPlayer.css';

const AudioPlayer = ({ sound }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);
  const canvasRef = useRef(null);
  const progressContainerRef = useRef(null);
  const animationRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceRef = useRef(null);
  const dataArrayRef = useRef(null);

  // Réinitialisation complète lorsque le son change
  useEffect(() => {
    const cleanup = () => {
      if (sourceRef.current) {
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }
      if (analyserRef.current) {
        analyserRef.current.disconnect();
        analyserRef.current = null;
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };

    cleanup(); // Nettoyer les ressources existantes

    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);

    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current.src = sound; // Forcer la mise à jour de la source
    }

    renderStaticWave(); // Afficher l'onde statique immédiatement

    return cleanup; // Nettoyer à la sortie ou au prochain changement
  }, [sound]);

  // Afficher une forme d'onde statique
  const renderStaticWave = () => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = 'rgba(27, 94, 32, 0.05)';
    ctx.fillRect(0, 0, width, height);

    ctx.beginPath();
    ctx.moveTo(0, height / 2);
    ctx.lineTo(width, height / 2);
    ctx.strokeStyle = 'rgba(27, 94, 32, 0.2)';
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.beginPath();
    let x = 0;
    const sliceWidth = width / 50;
    for (let i = 0; i < 50; i++) {
      const amp = Math.random() * 5 + 2;
      const y = height / 2 + Math.sin(i * 0.2) * amp;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
      x += sliceWidth;
    }
    ctx.strokeStyle = '#2e7d32';
    ctx.lineWidth = 1.5;
    ctx.stroke();
  };

  // Initialisation de l'audio et du visualiseur
  const setupAudioVisualizer = () => {
    if (!sound || !audioRef.current || sourceRef.current) return;

    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      audioContextRef.current = new AudioContext();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 128;
      const bufferLength = analyserRef.current.frequencyBinCount;
      dataArrayRef.current = new Uint8Array(bufferLength);

      sourceRef.current = audioContextRef.current.createMediaElementSource(audioRef.current);
      sourceRef.current.connect(analyserRef.current);
      analyserRef.current.connect(audioContextRef.current.destination);

      renderStaticWave();
    } catch (error) {
      console.error("Erreur lors de l'initialisation de l'audio:", error);
      renderStaticWave();
    }
  };

  // Visualisation de la forme d'onde active
  const renderWaveform = () => {
    if (!canvasRef.current || !analyserRef.current || !dataArrayRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    analyserRef.current.getByteFrequencyData(dataArrayRef.current);
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = 'rgba(27, 94, 32, 0.05)';
    ctx.fillRect(0, 0, width, height);

    ctx.beginPath();
    ctx.moveTo(0, height / 2);
    ctx.lineTo(width, height / 2);
    ctx.strokeStyle = 'rgba(27, 94, 32, 0.2)';
    ctx.lineWidth = 1;
    ctx.stroke();

    const barWidth = width / dataArrayRef.current.length;
    for (let i = 0; i < dataArrayRef.current.length; i++) {
      const barHeight = (dataArrayRef.current[i] / 255) * (height / 2);
      const x = i * barWidth;
      ctx.fillStyle = `rgba(46, 125, 50, ${0.5 + barHeight / height})`;
      ctx.fillRect(x, height / 2 - barHeight, barWidth - 1, barHeight);
      ctx.fillRect(x, height / 2, barWidth - 1, barHeight);
    }

    ctx.beginPath();
    ctx.moveTo(0, height / 2);
    let x = 0;
    const sliceWidth = width / dataArrayRef.current.length;
    for (let i = 0; i < dataArrayRef.current.length; i++) {
      const v = dataArrayRef.current[i] / 255.0;
      const y = height / 2 - v * (height / 4);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.quadraticCurveTo(x - sliceWidth / 2, y, x, y);
      x += sliceWidth;
    }
    ctx.lineTo(width, height / 2);
    ctx.strokeStyle = '#1b5e20';
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(0, height / 2);
    x = 0;
    for (let i = 0; i < dataArrayRef.current.length; i++) {
      const v = dataArrayRef.current[i] / 255.0;
      const y = height / 2 + v * (height / 4);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.quadraticCurveTo(x - sliceWidth / 2, y, x, y);
      x += sliceWidth;
    }
    ctx.lineTo(width, height / 2);
    ctx.strokeStyle = '#1b5e20';
    ctx.lineWidth = 2;
    ctx.stroke();

    animationRef.current = requestAnimationFrame(renderWaveform);
  };

  // Gestion de la lecture
  const handlePlay = () => {
    if (!audioRef.current) return;

    try {
      if (!sourceRef.current) {
        setupAudioVisualizer();
      }

      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume();
      }

      audioRef.current.play().then(() => {
        setIsPlaying(true);
        renderWaveform();
      }).catch(error => {
        console.error("Erreur lors de la lecture:", error);
        setIsPlaying(false);
        renderStaticWave();
      });
    } catch (error) {
      console.error("Erreur lors de la lecture:", error);
      setIsPlaying(false);
      renderStaticWave();
    }
  };

  // Gestion de la pause
  const handlePause = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      renderStaticWave();
    }
  };

  // Mise à jour de la progression
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
      setDuration(audioRef.current.duration || 0);
    }
  };

  // Gestion du clic sur la barre de progression
  const handleProgressClick = (e) => {
    if (!audioRef.current || !progressContainerRef.current) return;

    const rect = progressContainerRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    const newTime = (clickX / width) * audioRef.current.duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // Formatage du temps
  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  return sound ? (
    <div className={`sound-player ${isPlaying ? 'playing' : ''}`}>
      <audio
        ref={audioRef}
        src={sound}
        loop
        preload="auto"
        onTimeUpdate={handleTimeUpdate}
        onEnded={() => {
          setIsPlaying(false);
          renderStaticWave();
        }}
        onCanPlay={() => {
          if (!sourceRef.current) {
            setupAudioVisualizer();
          }
          setDuration(audioRef.current.duration || 0);
        }}
      >
        Votre navigateur ne supporte pas l'élément audio.
      </audio>

      <div className="audio-visualizer">
        <canvas
          ref={canvasRef}
          width="300"
          height="120"
          className="visualizer-canvas"
        />
      </div>

      <div className="time-display">
        <span className="current-time">{formatTime(currentTime)}</span>
        <span className="duration">{formatTime(duration)}</span>
      </div>

      <div
        className="progress-container"
        ref={progressContainerRef}
        onClick={handleProgressClick}
      >
        <div
          className="progress-bar"
          style={{ width: `${(currentTime / duration) * 100 || 0}%` }}
        >
          <div className="progress-handle" />
        </div>
      </div>

      <div className="audio-controls">
        <button
          onClick={handlePlay}
          disabled={isPlaying}
          className="play-btn"
        >
          <span className="btn-icon">▶</span> Écouter
        </button>
        <button
          onClick={handlePause}
          disabled={!isPlaying}
          className="pause-btn"
        >
          <span className="btn-icon">❚❚</span> Pause
        </button>
      </div>
    </div>
  ) : null;
};

export default AudioPlayer;